/**
 * Active if true / 1 / "1". Missing/null defaults to active (backend default).
 * Avoids Boolean("0") === true.
 * Uses string keys (not FIELD_NAMES) so style helpers can import this without
 * cycling through pricerTable → cell renderers → agGridCommonStyle.
 *
 * is_active / summary_id stay out of the AG Grid sort model so a clicked
 * column shows as sort 1. postSortRows applies inactive-last and, when no
 * visible column is sorted, newest (summary_id desc) within each group.
 */
export const PRICER_ACTIVE_SORT_COL_ID = "is_active";
export const PRICER_SUMMARY_SORT_COL_ID = "summary_id";

export function isPricerRowActive(row) {
  const value = row?.is_active ?? row?.summary_agg?.is_active;
  if (value === undefined || value === null || value === "") return true;
  return value === true || value === 1 || value === "1";
}

/** Leaf/group row used for sort — group aggData is often empty. */
export function getPricerSortRow(node) {
  if (node?.group) {
    return node.allLeafChildren?.[0]?.data ?? node.aggData ?? node.data;
  }
  return node?.data ?? node?.aggData;
}

function rowForActiveSort(params) {
  return getPricerSortRow(params?.node) ?? params?.data;
}

/** AG Grid sort value: 1 active / 0 inactive (desc puts inactive last). */
export function getPricerActiveSortValue(params) {
  return isPricerRowActive(rowForActiveSort(params)) ? 1 : 0;
}

export function isHiddenPricerSortCol(colId) {
  return (
    colId === PRICER_ACTIVE_SORT_COL_ID || colId === PRICER_SUMMARY_SORT_COL_ID
  );
}

/** User-facing sort keys only — hidden cols would make Tenor show as 2. */
export function visiblePricerSort(sortedColumns) {
  return (sortedColumns || [])
    .filter((col) => col?.sort && !isHiddenPricerSortCol(col.colId))
    .map((col, index) => ({
      colId: col.colId,
      sort: col.sort,
      sortIndex: index,
    }));
}

export function hasVisiblePricerSort(columnApi) {
  const state = columnApi?.getColumnState?.() || [];
  return state.some((col) => col.sort && !isHiddenPricerSortCol(col.colId));
}

/**
 * null = sort model already has no hidden keys.
 * Otherwise the visible-only state to apply (may be []).
 */
export function nextPricerSortState(sortedColumns) {
  const sorted = (sortedColumns || []).filter((col) => col?.sort);
  if (!sorted.some((col) => isHiddenPricerSortCol(col.colId))) return null;
  return visiblePricerSort(sorted);
}

function summaryIdOf(node) {
  const id = getPricerSortRow(node)?.summary_id;
  if (id == null || id === "") return 0;
  const numeric = Number(id);
  return Number.isNaN(numeric) ? 0 : numeric;
}

export function sortNodesBySummaryIdDesc(nodes) {
  if (!nodes?.length) return nodes;
  nodes.sort((a, b) => summaryIdOf(b) - summaryIdOf(a));
  return nodes;
}

/**
 * Treat inactive options as a second group: keep the current table sort
 * inside each group, then place the whole inactive group after active.
 * Mutates `nodes` in place (AG Grid postSortRows).
 */
export function partitionNodesInactiveLast(nodes) {
  if (!nodes?.length) return nodes;
  const active = [];
  const inactive = [];
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];
    if (isPricerRowActive(getPricerSortRow(node))) active.push(node);
    else inactive.push(node);
  }
  nodes.length = 0;
  for (let i = 0; i < active.length; i += 1) nodes.push(active[i]);
  for (let i = 0; i < inactive.length; i += 1) nodes.push(inactive[i]);
  return nodes;
}

/**
 * AG Grid 27 `postSortRows`: mutate params.nodes (typed) or params.rowNodes.
 * Do not also set legacy `postSort`.
 */
export function postSortPricerRows(params) {
  const nodes = params?.rowNodes ?? params?.nodes;
  if (!nodes?.length) return;
  if (!hasVisiblePricerSort(params?.columnApi)) {
    sortNodesBySummaryIdDesc(nodes);
  }
  partitionNodesInactiveLast(nodes);
}
